import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  computed,
  inject,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';

import { NotificationsStore, PERMISSIONS, PermissionService } from '@core/core.public-api';
import type { NotificationChannel } from '@core/core.public-api';
// Import profundo deliberado (igual que el resto del chrome): el barrel completo de
// shared arrastra map/charts al bundle inicial del shell.
import { IconComponent } from '@shared/ui/atoms/icon/icon.component';
import { SpinnerComponent } from '@shared/ui/atoms/spinner/spinner.component';
import { EmptyStateComponent } from '@shared/ui/molecules/empty-state/empty-state.component';
import { formatearFecha, tiempoRelativo } from '@shared/util/date.util';

const CANAL_LABEL: Record<NotificationChannel, string> = {
  inapp: 'In-app',
  push: 'Push',
  mail: 'Mail',
};

/**
 * Campana de notificaciones del topbar (chrome del shell).
 *
 * Vive en `layout/` — no en `features/` — porque la regla de dependencia
 * (`eslint-plugin-boundaries`) prohíbe `layout → features` pero permite
 * `layout → core`: el read-model (store/adapter/dominio) está en `core/notifications`
 * y este componente sólo lo consume (como los breadcrumbs consumen el Router).
 *
 * Visible para TODO backoffice logueado (badge de conteo + panel desplegable), pero
 * sólo consulta la API cuando el usuario tiene `notifications:read` — así evita el
 * 403 que el interceptor global toastearía ("UI oculta por UX, backend autoriza").
 * Sin ese permiso muestra un empty-state elegante sin tocar la red.
 */
@Component({
  selector: 'app-notification-bell',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, IconComponent, SpinnerComponent, EmptyStateComponent],
  template: `
    <div class="bell" [class.bell--open]="open()">
      <button
        type="button"
        class="bell__btn"
        [class.bell__btn--active]="open()"
        (click)="toggle()"
        [attr.aria-expanded]="open()"
        aria-haspopup="dialog"
        aria-label="Notificaciones"
      >
        <pvp-icon name="bell" size="md" />
        @if (showBadge()) {
          <span class="bell__count" aria-hidden="true">{{ countLabel() }}</span>
        }
      </button>

      @if (open()) {
        <div class="bell__backdrop" (click)="close()" aria-hidden="true"></div>

        <section class="bell__panel" role="dialog" aria-label="Notificaciones">
          <header class="bell__panel-head">
            <h2 class="bell__panel-title">Notificaciones</h2>
            <button
              type="button"
              class="bell__close"
              (click)="close()"
              aria-label="Cerrar notificaciones"
            >
              <pvp-icon name="x" size="sm" />
            </button>
          </header>

          <div class="bell__body">
            @if (store.loading()) {
              <div class="bell__center">
                <pvp-spinner size="md" label="Cargando notificaciones" />
              </div>
            } @else if (store.error()) {
              <div class="bell__error" role="alert">
                <span class="bell__error-icon" aria-hidden="true">
                  <pvp-icon name="alert-triangle" size="sm" />
                </span>
                <p class="bell__error-text">No se pudieron cargar las notificaciones.</p>
                <button type="button" class="bell__retry" (click)="refresh()">
                  <pvp-icon name="refresh" size="xs" />
                  Reintentar
                </button>
              </div>
            } @else if (store.entities().length === 0) {
              <pvp-empty-state
                icon="bell"
                title="Estás al día"
                description="No tenés notificaciones nuevas."
              />
            } @else {
              <ul class="bell__list">
                @for (n of store.entities(); track n.id) {
                  <li class="bell__item">
                    <div class="bell__item-top">
                      <span class="bell__item-title">{{ n.title }}</span>
                      <time
                        class="bell__item-time"
                        [attr.datetime]="n.createdAt"
                        [attr.title]="fechaAbsoluta(n.createdAt)"
                      >
                        {{ relativo(n.createdAt) }}
                      </time>
                    </div>
                    <p class="bell__item-body">{{ n.body }}</p>
                    <div class="bell__item-meta">
                      @for (c of n.channels; track c) {
                        <span class="bell__chip">{{ canalLabel(c) }}</span>
                      }
                      @if (n.status === 'fallida') {
                        <span class="bell__chip bell__chip--danger">Falló el envío</span>
                      } @else if (n.status === 'pendiente') {
                        <span class="bell__chip bell__chip--muted">Pendiente</span>
                      }
                    </div>
                  </li>
                }
              </ul>
            }
          </div>

          @if (canRead()) {
            <footer class="bell__foot">
              <a class="bell__all" routerLink="/notifications" (click)="close()">
                Ver todas
                <pvp-icon name="chevron-right" size="xs" />
              </a>
            </footer>
          }
        </section>
      }
    </div>
  `,
  styles: [
    `
      .bell {
        position: relative;
        display: inline-flex;
      }
      .bell__btn {
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 34px;
        height: 34px;
        padding: 0;
        background: transparent;
        border: none;
        border-radius: var(--pvp-radius-sm);
        color: var(--pvp-color-text-muted);
        cursor: pointer;
        transition:
          background-color 150ms ease,
          color 150ms ease;
      }
      .bell__btn:hover,
      .bell__btn--active {
        background: var(--pvp-color-surface-raised);
        color: var(--pvp-color-text);
      }
      .bell__count {
        position: absolute;
        top: 1px;
        right: 1px;
        min-width: 16px;
        height: 16px;
        padding: 0 4px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 999px;
        background: var(--pvp-color-danger);
        border: 1.5px solid var(--pvp-color-surface);
        color: var(--pvp-color-danger-contrast);
        font-size: 10px;
        font-weight: 700;
        line-height: 1;
      }
      .bell__backdrop {
        position: fixed;
        inset: 0;
        z-index: 90;
        background: transparent;
      }
      .bell__panel {
        position: absolute;
        top: calc(100% + 8px);
        right: 0;
        z-index: 91;
        width: min(360px, calc(100vw - 24px));
        max-height: min(70vh, 520px);
        display: flex;
        flex-direction: column;
        background: var(--pvp-color-surface);
        border: 1px solid var(--pvp-color-border);
        border-radius: var(--pvp-radius-md);
        box-shadow: var(--pvp-shadow-card);
        overflow: hidden;
      }
      .bell__panel-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        padding: 12px 14px;
        border-bottom: 1px solid var(--pvp-color-border);
      }
      .bell__panel-title {
        margin: 0;
        font-size: 13px;
        font-weight: 600;
        color: var(--pvp-color-text);
      }
      .bell__close {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        padding: 0;
        background: transparent;
        border: none;
        border-radius: var(--pvp-radius-sm);
        color: var(--pvp-color-text-muted);
        cursor: pointer;
        transition:
          background-color 150ms ease,
          color 150ms ease;
      }
      .bell__close:hover {
        background: var(--pvp-color-surface-raised);
        color: var(--pvp-color-text);
      }
      .bell__body {
        min-height: 120px;
        overflow-y: auto;
      }
      .bell__center {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 32px 0;
      }
      .bell__error {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        padding: 28px 20px;
        text-align: center;
      }
      .bell__error-icon {
        color: var(--pvp-color-warning);
      }
      .bell__error-text {
        margin: 0;
        font-size: 13px;
        color: var(--pvp-color-text-muted);
      }
      .bell__retry {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 5px 10px;
        background: transparent;
        border: 1px solid var(--pvp-color-border-strong);
        border-radius: var(--pvp-radius-sm);
        color: var(--pvp-color-text-muted);
        font-size: 12.5px;
        cursor: pointer;
      }
      .bell__retry:hover {
        background: var(--pvp-color-surface-raised);
        color: var(--pvp-color-text);
      }
      .bell__list {
        list-style: none;
        margin: 0;
        padding: 0;
      }
      .bell__item {
        display: flex;
        flex-direction: column;
        gap: 4px;
        padding: 11px 14px;
        border-bottom: 1px solid var(--pvp-color-border);
      }
      .bell__item:last-child {
        border-bottom: none;
      }
      .bell__item-top {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 10px;
      }
      .bell__item-title {
        min-width: 0;
        font-size: 13px;
        font-weight: 600;
        color: var(--pvp-color-text);
      }
      .bell__item-time {
        flex: none;
        font-size: 11px;
        color: var(--pvp-color-text-subtle);
        white-space: nowrap;
      }
      .bell__item-body {
        margin: 0;
        font-size: 12.5px;
        line-height: 1.45;
        color: var(--pvp-color-text-muted);
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
      .bell__item-meta {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        margin-top: 2px;
      }
      .bell__chip {
        padding: 1px 7px;
        border-radius: 999px;
        border: 1px solid var(--pvp-color-border);
        background: var(--pvp-color-surface-raised);
        color: var(--pvp-color-text-muted);
        font-size: 10.5px;
        font-weight: 500;
      }
      .bell__chip--danger {
        border-color: var(--pvp-color-danger);
        color: var(--pvp-color-danger);
        background: var(--pvp-color-danger-tint);
      }
      .bell__chip--muted {
        color: var(--pvp-color-text-subtle);
      }
      .bell__foot {
        padding: 8px 14px;
        border-top: 1px solid var(--pvp-color-border);
        text-align: center;
      }
      .bell__all {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-size: 12.5px;
        font-weight: 600;
        color: var(--pvp-color-primary);
        text-decoration: none;
      }
      .bell__all:hover {
        text-decoration: underline;
      }
    `,
  ],
})
export class NotificationBellComponent implements OnInit {
  protected readonly store = inject(NotificationsStore);
  private readonly permissions = inject(PermissionService);

  /** ¿El usuario puede leer notificaciones? Gobierna si tocamos la API (evita el 403 global). */
  protected readonly canRead = this.permissions.can(PERMISSIONS.NOTIFICATIONS_READ);

  protected readonly open = signal(false);

  protected readonly showBadge = computed(() => this.canRead() && this.store.count() > 0);
  protected readonly countLabel = computed(() => {
    const c = this.store.count();
    return c > 99 ? '99+' : String(c);
  });

  ngOnInit(): void {
    // Sólo precargamos el badge si el usuario tiene el permiso (si no, no gatillamos 403).
    if (this.canRead()) {
      void this.store.init();
    }
  }

  protected toggle(): void {
    const next = !this.open();
    this.open.set(next);
    // Al abrir, refrescamos para ver lo último (sólo con permiso).
    if (next && this.canRead()) {
      void this.store.load();
    }
  }

  protected close(): void {
    this.open.set(false);
  }

  protected refresh(): void {
    if (this.canRead()) {
      void this.store.load();
    }
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    if (this.open()) {
      this.close();
    }
  }

  protected relativo(iso: string): string {
    return tiempoRelativo(iso);
  }

  protected fechaAbsoluta(iso: string): string {
    return formatearFecha(iso);
  }

  protected canalLabel(canal: NotificationChannel): string {
    return CANAL_LABEL[canal];
  }
}
